import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '@/components/ui/sheet';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from '@/components/ui/scroll-area';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { MultiSelect } from '@/components/ui/multiselect';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ChevronDown, Save, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useUserPreferences } from '@/providers/UserPreferencesProvider';
import type { ProjectTemplate, CreateTemplateData } from '@/types/template.types';
import { toast } from 'sonner';

// Mock data - same as project form
const priorityOptions = ['Low', 'Medium', 'High', 'Critical'];
const typeOptions = ['Fixed Price', 'Time & Materials', 'Retainer'];
const currencyOptions = ['GBP', 'USD', 'EUR', 'AUD', 'CAD', 'JPY', 'CHF'];
const clientOptions = [
  'Acme Corp',
  'TechStart Inc',
  'Global Solutions',
  'Innovation Labs',
  'Alpha Industries',
  'Beta Technologies',
  'Gamma Enterprises',
  'Delta Systems',
];
const tagOptions = [
  'High Priority',
  'Strategic',
  'Quick Win',
  'Long Term',
  'Innovation',
  'Cost Reduction',
  'Customer Facing',
  'Internal Process',
];

// Form validation schema
const templateFormSchema = z.object({
  templateName: z.string().min(1, 'Template name is required').max(80, 'Template name must not exceed 80 characters'),
  templateDescription: z.string().max(200, 'Template description must not exceed 200 characters').optional(),
  description: z.string().max(500, 'Description must not exceed 500 characters').optional(),
  priority: z.string().optional(),
  type: z.string().optional(),
  location: z.string().optional(),
  unit: z.enum(['days', 'weeks', 'months', 'years']).optional(),
  currencyCode: z.string().optional(),
  clients: z.array(z.string()).optional(),
  tags:z.array(z.string()).optional(),
});

type TemplateFormValues = z.infer<typeof templateFormSchema>;

interface TemplateFormSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  template?: ProjectTemplate;
  onSave: (data: CreateTemplateData) => void;
}

export function TemplateFormSheet({
  open,
  onOpenChange,
  mode,
  template,
  onSave,
}: TemplateFormSheetProps) {
  const { preferences } = useUserPreferences();
  const [isSaving, setIsSaving] = useState(false);

  // Accordion states - all open by default for template creation
  const [generalOpen, setGeneralOpen] = useState(true);
  const [timelineOpen, setTimelineOpen] = useState(true);
  const [managementOpen, setManagementOpen] = useState(true);

  // Determine which side to open from based on sidebar position
  const sheetSide = preferences.sidebarPosition === 'right' ? 'left' : 'right';

  const form = useForm<TemplateFormValues>({
    resolver: zodResolver(templateFormSchema),
    defaultValues: {
      templateName: template?.templateName || '',
      templateDescription: template?.templateDescription || '',
      description: template?.description || '',
      priority: template?.priority || '',
      type: template?.type || '',
      location: template?.location || '',
      unit: template?.unit || 'days',
      currencyCode: template?.currencyCode || '',
      clients: template?.clients || [],
      tags: template?.tags || [],
    },
  });

  // Reset form when template changes or modal opens/closes
  useEffect(() => {
    if (open) {
      form.reset({
        templateName: template?.templateName || '',
        templateDescription: template?.templateDescription || '',
        description: template?.description || '',
        priority: template?.priority || '',
        type: template?.type || '',
        location: template?.location || '',
        unit: template?.unit || 'days',
        currencyCode: template?.currencyCode || '',
        clients: template?.clients || [],
        tags: template?.tags || [],
      });
    }
  }, [open, template, form]);

  const handleSubmit = async (data: TemplateFormValues) => {
    try {
      setIsSaving(true);
      await onSave(data);
      toast.success(mode === 'create' ? 'Template created successfully' : 'Template updated successfully');
      onOpenChange(false);
    } catch (error) {
      toast.error(mode === 'create' ? 'Failed to create template' : 'Failed to update template');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    form.reset();
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side={sheetSide} className="w-full sm:max-w-2xl p-0 flex flex-col h-full">
        {/* Fixed Header */}
        <div className="border-b bg-background p-6 pb-4 flex-shrink-0">
          <SheetHeader>
            <SheetTitle>
              {mode === 'create' ? 'Create Template' : 'Edit Template'}
            </SheetTitle>
            <SheetDescription>
              {mode === 'create'
                ? 'Create a reusable template with pre-filled fields for faster project creation.'
                : 'Update your template configuration.'}
            </SheetDescription>
          </SheetHeader>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col flex-1 min-h-0">
            {/* Scrollable Content */}
            <ScrollArea className="flex-1 min-h-0">
              <ScrollAreaViewport className="h-full max-h-full">
                <div className="px-6 py-4 space-y-6">
              {/* Template Identity Section */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold mb-4">Template Identity</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Give your template a unique name and optional description to help identify its purpose.
                  </p>
                </div>

                <FormField
                  control={form.control}
                  name="templateName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Template Name <span className="text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input placeholder="e.g., Software Development Project" {...field} />
                      </FormControl>
                      <FormDescription>
                        A unique name to identify this template
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="templateDescription"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Template Description</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Describe what this template is for and when to use it..."
                          className="resize-none"
                          rows={3}
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        Optional description to explain the template's purpose
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <Separator />

              {/* General Information Accordion */}
              <Collapsible open={generalOpen} onOpenChange={setGeneralOpen}>
                <CollapsibleTrigger className="flex items-center justify-between w-full group">
                  <h3 className="text-lg font-semibold">General Information</h3>
                  <ChevronDown
                    className={cn(
                      'h-5 w-5 text-muted-foreground transition-transform duration-200',
                      generalOpen && 'transform rotate-180'
                    )}
                  />
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-4 mt-4">
                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Project Description</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Default project description..."
                            className="resize-none"
                            rows={4}
                            {...field}
                          />
                        </FormControl>
                        <FormDescription>
                          This will pre-fill the project description field
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="priority"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Priority</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select priority" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {priorityOptions.map((option) => (
                              <SelectItem key={option} value={option}>
                                {option}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="type"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Type</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {typeOptions.map((option) => (
                              <SelectItem key={option} value={option}>
                                {option}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="location"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Location</FormLabel>
                        <FormControl>
                          <Input placeholder="e.g., London, UK" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CollapsibleContent>
              </Collapsible>

              <Separator />

              {/* Timeline Accordion */}
              <Collapsible open={timelineOpen} onOpenChange={setTimelineOpen}>
                <CollapsibleTrigger className="flex items-center justify-between w-full group">
                  <h3 className="text-lg font-semibold">Timeline</h3>
                  <ChevronDown
                    className={cn(
                      'h-5 w-5 text-muted-foreground transition-transform duration-200',
                      timelineOpen && 'transform rotate-180'
                    )}
                  />
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-4 mt-4">
                  <FormField
                    control={form.control}
                    name="unit"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Duration Unit</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select unit" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="days">Days</SelectItem>
                            <SelectItem value="weeks">Weeks</SelectItem>
                            <SelectItem value="months">Months</SelectItem>
                            <SelectItem value="years">Years</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormDescription>
                          Default unit for project duration
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CollapsibleContent>
              </Collapsible>

              <Separator />

              {/* Management Accordion */}
              <Collapsible open={managementOpen} onOpenChange={setManagementOpen}>
                <CollapsibleTrigger className="flex items-center justify-between w-full group">
                  <h3 className="text-lg font-semibold">Management</h3>
                  <ChevronDown
                    className={cn(
                      'h-5 w-5 text-muted-foreground transition-transform duration-200',
                      managementOpen && 'transform rotate-180'
                    )}
                  />
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-4 mt-4">
                  <FormField
                    control={form.control}
                    name="currencyCode"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Currency Code</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select currency" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {currencyOptions.map((option) => (
                              <SelectItem key={option} value={option}>
                                {option}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="clients"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Clients</FormLabel>
                        <FormControl>
                          <MultiSelect
                            options={clientOptions.map((opt) => ({ label: opt, value: opt }))}
                            value={field.value || []}
                            onValueChange={field.onChange}
                            placeholder="Select clients"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="tags"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Tags</FormLabel>
                        <FormControl>
                          <MultiSelect
                            options={tagOptions.map((opt) => ({ label: opt, value: opt }))}
                            value={field.value || []}
                            onValueChange={field.onChange}
                            placeholder="Select tags"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CollapsibleContent>
              </Collapsible>
                </div>
              </ScrollAreaViewport>
              <ScrollAreaScrollbar orientation="vertical" />
            </ScrollArea>

            {/* Sticky Footer */}
            <SheetFooter className="sticky bottom-0 bg-background border-t px-6 py-4 mt-auto">
              <div className="flex items-center justify-end gap-3 w-full">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                  disabled={isSaving}
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
                <Button type="submit" disabled={isSaving}>
                  <Save className="h-4 w-4 mr-2" />
                  {isSaving ? 'Saving...' : mode === 'create' ? 'Create Template' : 'Save Changes'}
                </Button>
              </div>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
}
